import React, { useState } from 'react'
import { ArrowLeftRight, FileText, Code, Download, Copy } from 'lucide-react'
import { jsonToYaml, yamlToJson } from '../utils/jsonUtils'
import { downloadFile, copyToClipboard } from '../utils/fileUtils'

const ConversionPanel = ({ jsonData, onYamlToJson }) => {
  const [yamlContent, setYamlContent] = useState('')
  const [isConverting, setIsConverting] = useState(false)

  const handleJsonToYaml = () => {
    if (!jsonData) {
      console.error('No JSON data to convert')
      return
    }

    try {
      setIsConverting(true)
      const yaml = jsonToYaml(jsonData)
      setYamlContent(yaml)
      console.log('Converted to YAML successfully')
    } catch (error) {
      console.error('Failed to convert to YAML:', error.message)
    } finally {
      setIsConverting(false)
    }
  }

  const handleYamlToJson = () => {
    if (!yamlContent.trim()) {
      console.error('No YAML content to convert')
      return
    }

    try {
      setIsConverting(true)
      const json = yamlToJson(yamlContent)
      onYamlToJson(json)
      console.log('Converted to JSON successfully')
    } catch (error) {
      console.error('Failed to convert from YAML:', error.message)
    } finally {
      setIsConverting(false)
    }
  }

  const handleDownloadYaml = () => {
    if (!yamlContent) {
      console.error('No YAML content to download')
      return
    }

    try {
      downloadFile('data.yaml', yamlContent, 'text/yaml')
      console.log('YAML file downloaded')
    } catch (error) {
      console.error('Failed to download YAML file:', error)
    }
  }

  const handleCopyYaml = async () => {
    if (!yamlContent) {
      console.error('No YAML content to copy')
      return
    }

    try {
      await copyToClipboard(yamlContent)
      console.log('YAML copied to clipboard')
    } catch (error) {
      console.error('Failed to copy YAML to clipboard:', error)
    }
  }

  return (
    <div className="panel">
      <div className="panel-header">
        <span>JSON ⇄ YAML Converter</span>
        <div className="btn-group">
          <button
            className="btn btn-secondary"
            onClick={handleJsonToYaml}
            disabled={!jsonData || isConverting}
            title="Convert JSON to YAML"
          >
            <ArrowLeftRight size={16} />
            JSON → YAML
          </button>
        </div>
      </div>
      
      <div className="panel-content">
        <div style={{ padding: '16px', borderBottom: '1px solid var(--border-color)' }}>
          <textarea
            value={yamlContent}
            onChange={(e) => setYamlContent(e.target.value)}
            placeholder="YAML content will appear here..."
            style={{
              width: '100%',
              height: '300px',
              padding: '12px',
              border: '1px solid var(--border-color)',
              borderRadius: '6px',
              backgroundColor: 'var(--input-bg)',
              color: 'var(--text-color)',
              fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
              fontSize: '14px',
              resize: 'vertical'
            }}
          />
        </div>
        
        <div className="toolbar" style={{ margin: 0, borderRadius: 0 }}>
          <div className="btn-group">
            <button
              className="btn btn-success"
              onClick={handleYamlToJson}
              disabled={!yamlContent.trim() || isConverting}
              title="Convert YAML to JSON"
            >
              <ArrowLeftRight size={16} />
              YAML → JSON
            </button>
          </div>
          
          <div className="btn-group">
            <button
              className="btn btn-secondary"
              onClick={handleDownloadYaml}
              disabled={!yamlContent}
              title="Download YAML file"
            >
              <Download size={16} />
              Download
            </button>
            
            <button
              className="btn btn-secondary"
              onClick={handleCopyYaml}
              disabled={!yamlContent}
              title="Copy YAML to clipboard"
            >
              <Copy size={16} />
              Copy
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ConversionPanel
